// ============================================
// About.js — Profile photo, bio, skills tags,
//            and social links
// ============================================
import React, { useEffect, useRef } from 'react';
import { FiMapPin, FiMail, FiCalendar } from 'react-icons/fi';
import { FaGithub, FaLinkedin, FaInstagram } from 'react-icons/fa';
import '../styles/About.css';
import useScrollReveal from '../hooks/useScrollReveal';

// Personal info rows
const info = [
  { icon: <FiMapPin />,    label: 'Location',  value: 'Gandhinagar, India' },
  { icon: <FiMail />,      label: 'Email',     value: 'hetrathod49@email.com' },
  { icon: <FiCalendar />,  label: 'Available', value: 'Immediately' },
];

// Skills tag cloud for about section
const techTags = [
  'React.js', 'Node.js', 'JavaScript', 'GitHub',
  'C', 'MongoDB', 'Express.js', 'Tailwind CSS',
  'Git', 'REST APIs', 'Postman', 'Figma',
];

const About = () => {
  const sectionRef = useRef(null);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          sectionRef.current?.classList.add('visible');
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // 3D tilt on photo card
  useEffect(() => {
    const card = document.querySelector('.about-photo-frame');
    if (!card) return;
    const onMove = (e) => {
      const rect   = card.getBoundingClientRect();
      const x      = (e.clientX - rect.left) / rect.width  - 0.5;
      const y      = (e.clientY - rect.top)  / rect.height - 0.5;
      card.style.transform = `perspective(600px) rotateY(${x * 18}deg) rotateX(${-y * 18}deg) scale(1.03)`;
    };
    const onLeave = () => { card.style.transform = 'perspective(600px) rotateY(0) rotateX(0) scale(1)'; };
    card.addEventListener('mousemove', onMove);
    card.addEventListener('mouseleave', onLeave);
    return () => { card.removeEventListener('mousemove', onMove); card.removeEventListener('mouseleave', onLeave); };
  }, []);

  return (
    <section id="about" className="about section-padding">
      {/* Background orb */}
      <div className="glow-orb about-orb" />

      <div className="container" ref={sectionRef}>
        {/* Section header */}
        <div className="section-header reveal-item">
          <span className="section-tag">Who I Am</span>
          <h2 className="section-title">About <span className="gradient-text">Me</span></h2>
          <p className="section-subtitle">A passionate developer who loves building things that make a difference.</p>
        </div>

        <div className="about-grid">
          {/* Left: Photo card */}
          <div className="about-photo-col reveal-item">
            <div className="about-photo-wrapper">
              <div className="about-photo-frame">
                {/* Replace this div with an <img> tag pointing to your photo */}
                <div className="about-photo-placeholder">
                  <span>YOUR<br/>PHOTO</span>
                </div>
                {/* Decorative badge */}
                <div className="about-exp-badge">
                  <span className="exp-number">2+</span>
                  <span className="exp-label">Years<br/>Experience</span>
                </div>
              </div>
              {/* Floating tech icons */}
              <div className="floating-tag ft-1">React</div>
              <div className="floating-tag ft-2">Node.js</div>
              <div className="floating-tag ft-3">C</div>
            </div>
          </div>

          {/* Right: Bio & info */}
          <div className="about-bio-col reveal-item reveal-delay-1">
            <h3 className="about-name">Hello! I'm <span className="gradient-text">Het Rathod</span></h3>
            <p className="about-text">
              I'm a passionate Full Stack Developer based in India, specializing in
              building exceptional digital experiences. With a strong foundation in
              both frontend and backend technologies, I love turning complex problems
              into simple, beautiful, and intuitive solutions.
            </p>
            <p className="about-text">
              When I'm not coding, you'll find me exploring new technologies,
              contributing to open-source projects, or leveling up my design skills.
              I believe great software is born at the intersection of clean code and
              thoughtful design.
            </p>

            {/* Info rows */}
            <div className="about-info">
              {info.map((item, i) => (
                <div key={i} className="info-row">
                  <span className="info-icon">{item.icon}</span>
                  <span className="info-label">{item.label}:</span>
                  <span className="info-value">{item.value}</span>
                </div>
              ))}
            </div>

            {/* Social links */}
            <div className="about-socials">
              <a href="https://github.com" target="_blank" rel="noreferrer" className="about-social-btn">
                <FaGithub /> GitHub
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="about-social-btn">
                <FaLinkedin /> LinkedIn
              </a>
              <a href="https://instagram.com" target="_blank" rel="noreferrer" className="about-social-btn">
                <FaInstagram /> Instagram
              </a>
            </div>

            {/* Tech tag cloud */}
            <div className="tech-tags">
              {techTags.map((tag, i) => (
                <span key={i} className="tech-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
